import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { applyTheme, type Theme } from '@/lib/theme';
import { applyHeadingFont, detectPresetIds, loadPresetFonts } from '@/lib/theme-presets';

export function useTheme() {
  const query = useQuery({
    queryKey: ['theme'],
    queryFn: () => api.get<{ theme: Theme }>('/api/public/theme'),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.data?.theme) {
      applyTheme(query.data.theme);
      const ids = detectPresetIds(query.data.theme);
      loadPresetFonts(ids);
      applyHeadingFont(ids);
    }
  }, [query.data]);

  return query;
}

export function usePlatformTheme() {
  return useQuery({
    queryKey: ['platform', 'theme'],
    queryFn: () => api.get<{ theme: Theme; default: Theme }>('/api/platform/settings/theme'),
  });
}

export function useSaveTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (theme: Theme) => api.put<{ theme: Theme }>('/api/platform/settings/theme', theme),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'theme'] });
      qc.invalidateQueries({ queryKey: ['theme'] });
    },
  });
}

export function useResetTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ theme: Theme }>('/api/platform/settings/theme/reset'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'theme'] });
      qc.invalidateQueries({ queryKey: ['theme'] });
    },
  });
}

/** Dark mode persisted in localStorage + applied to <html> class */
const DARK_KEY = 'cms-hub-dark';

export function initDarkMode() {
  if (typeof document === 'undefined') return;
  const saved = localStorage.getItem(DARK_KEY);
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = saved === null ? prefersDark : saved === '1';
  document.documentElement.classList.toggle('dark', isDark);
}

export function toggleDarkMode() {
  if (typeof document === 'undefined') return false;
  const isDark = document.documentElement.classList.toggle('dark');
  localStorage.setItem(DARK_KEY, isDark ? '1' : '0');
  return isDark;
}

export function useIsDark() {
  // Not reactive — components using it should re-render via parent state
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}
